import { useEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { replaceWithRegion } from 'actions/NavigationActions'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { useTypeaheadExtractionRefresh } from './useTypeaheadExtractionRefresh'
import {
  queryKeyAdults,
  queryKeyAmenities,
  queryKeyCheckIn,
  queryKeyCheckOut,
  queryKeyChildren,
  queryKeyDestinationId,
  queryKeyExtracting,
  queryKeyFlexibleMonths,
  queryKeyFlexibleNights,
  queryKeyInclusions,
  queryKeyInclusionGap,
  queryKeyTags,
  queryKeyChains,
  chainsDelimiter,
  queryKeySubChains,
  subChainsDelimiter,
  queryKeyLandmarkId,
  queryKeyLat,
  queryKeyLng,
  queryKeyPriceGte,
  queryKeyPriceLte,
  queryKeyPricingType,
  queryKeyPropertyId,
  queryKeyPropertyTypes,
  queryKeySortBy,
  queryKeyPages,
  queryKeyQuery,
  queryKeySemanticRerank,
  queryKeyIsQueryWellSupported,
  queryKeyTypeaheadId,
} from 'constants/url'
import { queryNullChildren, ANYWHERE_PLACE_ID } from 'constants/search'
import { deleteSearchParams, setManySearchParamValues } from 'lib/url/searchUrlUtils'
import { searchQueryParams } from 'lib/search/searchUtils'
import {
  areSearchDateSelectionsEqual,
  getSearchDateSelectionFromExtractedSearchParams,
  getTypeaheadDateConfirmationBaseline,
  isSearchDateSelectionExtractedOverride,
  markTypeaheadDateAcknowledgementRequired,
} from 'lib/search/typeaheadDateConfirmationUtils'

/**
 * Maps extracted typeahead streaming data to URL search parameters.
 * Uses mapStreamingDataToExtractedParams for extraction, then applies user preference overrides.
 * @param streamingData The streaming data containing extracted information
 * @param query The natural language query string to use for name fields
 * @param currentSearch The current URL search string (to preserve user-entered values)
 * @returns Array of URL parameters to set
 */
function mapExtractedDataToUrlParams(
  streamingData: App.TypeaheadStreamingData,
  currentSearch: string,
): Array<{ paramName: string; paramValue: string | number | undefined }> {
  const paramsToSet: Array<{ paramName: string; paramValue: string | number | undefined }> = []
  const currentParams = new URLSearchParams(currentSearch)

  // Get extracted data from finalSearchItem if available
  const searchItem = streamingData.finalSearchItem?.data
  const extractedData = searchItem?.searchType === 'searchItemV3' ? searchItem.searchItem.extractedData : undefined

  // If no extracted data, set defaults and return
  if (!extractedData) {
    paramsToSet.push({ paramName: queryKeyDestinationId, paramValue: ANYWHERE_PLACE_ID })
    paramsToSet.push({ paramName: queryKeyAdults, paramValue: '2' })
    paramsToSet.push({ paramName: queryKeyChildren, paramValue: queryNullChildren })
    paramsToSet.push({ paramName: queryKeyExtracting, paramValue: undefined })
    return paramsToSet
  }

  // Location params based on searchType
  const { searchType } = extractedData

  if ('property' in extractedData && extractedData.property) {
    paramsToSet.push({ paramName: queryKeyPropertyId, paramValue: extractedData.property.propertyId })
  } else if (searchType === 'latLong' && extractedData.lat && extractedData.lng) {
    paramsToSet.push({ paramName: queryKeyLat, paramValue: extractedData.lat })
    paramsToSet.push({ paramName: queryKeyLng, paramValue: extractedData.lng })
  } else if (searchType === 'landmark' && extractedData.placeIds && extractedData.placeIds.length > 0) {
    paramsToSet.push({ paramName: queryKeyLandmarkId, paramValue: extractedData.placeIds.join(',') })
  } else if (searchType === 'destination' && extractedData.placeIds && extractedData.placeIds.length > 0) {
    paramsToSet.push({ paramName: queryKeyDestinationId, paramValue: extractedData.placeIds.join(',') })
  } else {
    paramsToSet.push({ paramName: queryKeyDestinationId, paramValue: ANYWHERE_PLACE_ID })
  }
  if (extractedData.query) {
    paramsToSet.push({ paramName: queryKeyQuery, paramValue: extractedData.query })
  }
  if (extractedData.semanticRerank === true) {
    paramsToSet.push({ paramName: queryKeySemanticRerank, paramValue: 'true' })
  }
  const isQueryWellSupported = extractedData.isQueryWellSupported ?? streamingData.isQueryWellSupported?.data
  if (isQueryWellSupported === false) {
    paramsToSet.push({ paramName: queryKeyIsQueryWellSupported, paramValue: 'false' })
  }

  // Travel dates: extracted specific/flexible dates override prior picker values,
  // unless the user manually changed dates after extraction (keep their URL dates).
  // If extraction found no date signal, preserve the current picker/URL dates.
  // Shared-link URL dates alone do not block NL overwrite when the query extracts
  // new dates (product: NL date phrases win over prior URL dates).
  const userCheckIn = currentParams.get(queryKeyCheckIn)
  const userCheckOut = currentParams.get(queryKeyCheckOut)
  const userFlexibleNights = currentParams.get(queryKeyFlexibleNights)
  const userFlexibleMonths = currentParams.get(queryKeyFlexibleMonths)
  const extractedDateSelection = getSearchDateSelectionFromExtractedSearchParams(extractedData)
  const hasUserOwnedDates = !!streamingData.userSelectedDates || !!streamingData.userHasInteractedWithDates

  if (hasUserOwnedDates && (userCheckIn || userCheckOut || userFlexibleNights || userFlexibleMonths)) {
    if (userCheckIn) paramsToSet.push({ paramName: queryKeyCheckIn, paramValue: userCheckIn })
    if (userCheckOut) paramsToSet.push({ paramName: queryKeyCheckOut, paramValue: userCheckOut })
    if (userFlexibleNights) paramsToSet.push({ paramName: queryKeyFlexibleNights, paramValue: userFlexibleNights })
    if (userFlexibleMonths) paramsToSet.push({ paramName: queryKeyFlexibleMonths, paramValue: userFlexibleMonths })
  } else if (extractedDateSelection?.type === 'specific') {
    paramsToSet.push({ paramName: queryKeyCheckIn, paramValue: extractedDateSelection.checkIn })
    paramsToSet.push({ paramName: queryKeyCheckOut, paramValue: extractedDateSelection.checkOut })
  } else if (extractedDateSelection?.type === 'flexible') {
    paramsToSet.push({ paramName: queryKeyFlexibleNights, paramValue: extractedDateSelection.flexibleNights })
    paramsToSet.push({ paramName: queryKeyFlexibleMonths, paramValue: extractedDateSelection.flexibleMonths })
  } else {
    if (userCheckIn) paramsToSet.push({ paramName: queryKeyCheckIn, paramValue: userCheckIn })
    if (userCheckOut) paramsToSet.push({ paramName: queryKeyCheckOut, paramValue: userCheckOut })
    if (userFlexibleNights) paramsToSet.push({ paramName: queryKeyFlexibleNights, paramValue: userFlexibleNights })
    if (userFlexibleMonths) paramsToSet.push({ paramName: queryKeyFlexibleMonths, paramValue: userFlexibleMonths })
  }

  // Occupancy - extracted values take precedence, fallback to user-entered values
  const userAdults = currentParams.get(queryKeyAdults)
  const userChildren = currentParams.get(queryKeyChildren)

  if (extractedData.occupancy && extractedData.occupancy.length > 0) {
    const adults = extractedData.occupancy.map(room => room.adults).join(',')
    const children = extractedData.occupancy.map(room =>
      room.childrenAge && room.childrenAge.length > 0 ? room.childrenAge.join(';') : 'none',
    ).join(',')
    paramsToSet.push({ paramName: queryKeyAdults, paramValue: adults })
    paramsToSet.push({ paramName: queryKeyChildren, paramValue: children })
  } else if (userAdults) {
    // Preserve user's selection if extraction returned no occupancy
    paramsToSet.push({ paramName: queryKeyAdults, paramValue: userAdults })
    if (userChildren) paramsToSet.push({ paramName: queryKeyChildren, paramValue: userChildren })
  }

  // Tags - user-applied values take precedence
  const userTags = currentParams.get(queryKeyTags)
  if (userTags) {
    paramsToSet.push({ paramName: queryKeyTags, paramValue: userTags })
  } else if (extractedData.tags && extractedData.tags.length > 0) {
    paramsToSet.push({ paramName: queryKeyTags, paramValue: extractedData.tags.join(',') })
  }

  // Chains - user-applied values take precedence
  const userChains = currentParams.get(queryKeyChains)
  if (userChains) {
    paramsToSet.push({ paramName: queryKeyChains, paramValue: userChains })
  } else if (extractedData.chains && extractedData.chains.length > 0) {
    paramsToSet.push({ paramName: queryKeyChains, paramValue: extractedData.chains.join(chainsDelimiter) })
  }

  // SubChains - user-applied values take precedence
  const userSubChains = currentParams.get(queryKeySubChains)
  if (userSubChains) {
    paramsToSet.push({ paramName: queryKeySubChains, paramValue: userSubChains })
  } else if (extractedData.subChains && extractedData.subChains.length > 0) {
    paramsToSet.push({ paramName: queryKeySubChains, paramValue: extractedData.subChains.join(subChainsDelimiter) })
  }

  // Inclusions - user-applied values take precedence
  const userInclusions = currentParams.get(queryKeyInclusions)
  if (userInclusions) {
    paramsToSet.push({ paramName: queryKeyInclusions, paramValue: userInclusions })
  } else if (extractedData.inclusions && extractedData.inclusions.length > 0) {
    paramsToSet.push({ paramName: queryKeyInclusions, paramValue: extractedData.inclusions.join(',') })
  }

  // Inclusion gap
  if (extractedData.inclusionGap) {
    paramsToSet.push({ paramName: queryKeyInclusionGap, paramValue: extractedData.inclusionGap })
  }

  // Amenities - user-applied values take precedence
  const userAmenities = currentParams.get(queryKeyAmenities)
  if (userAmenities) {
    paramsToSet.push({ paramName: queryKeyAmenities, paramValue: userAmenities })
  } else if (extractedData.amenities && extractedData.amenities.length > 0) {
    paramsToSet.push({ paramName: queryKeyAmenities, paramValue: extractedData.amenities.join(',') })
  }

  // Accommodation types - user-applied values take precedence
  const userPropertyTypes = currentParams.get(queryKeyPropertyTypes)
  if (userPropertyTypes) {
    paramsToSet.push({ paramName: queryKeyPropertyTypes, paramValue: userPropertyTypes })
  } else if (extractedData.propertyTypes && extractedData.propertyTypes.length > 0) {
    paramsToSet.push({ paramName: queryKeyPropertyTypes, paramValue: extractedData.propertyTypes.join(',') })
  }

  // SortBy - user-applied values take precedence
  const userSortBy = currentParams.get(queryKeySortBy)
  if (userSortBy) {
    paramsToSet.push({ paramName: queryKeySortBy, paramValue: userSortBy })
  } else if (extractedData.sortBy) {
    paramsToSet.push({ paramName: queryKeySortBy, paramValue: extractedData.sortBy })
  }

  // Price filters - user-applied values take precedence. Natural-language
  // values retain their pricing type so svc-search can resolve their basis.
  const userPriceGte = currentParams.get(queryKeyPriceGte)
  const userPriceLte = currentParams.get(queryKeyPriceLte)
  if (userPriceGte || userPriceLte) {
    if (userPriceGte) paramsToSet.push({ paramName: queryKeyPriceGte, paramValue: userPriceGte })
    if (userPriceLte) paramsToSet.push({ paramName: queryKeyPriceLte, paramValue: userPriceLte })
  } else {
    if (extractedData.priceGte) paramsToSet.push({ paramName: queryKeyPriceGte, paramValue: extractedData.priceGte })
    if (extractedData.priceLte) paramsToSet.push({ paramName: queryKeyPriceLte, paramValue: extractedData.priceLte })
    if (extractedData.pricingType) paramsToSet.push({ paramName: queryKeyPricingType, paramValue: extractedData.pricingType })
  }

  // Remove extracting=true param
  paramsToSet.push({ paramName: queryKeyExtracting, paramValue: undefined })

  return paramsToSet
}

interface UseTypeaheadExtractionUrlUpdateParams {
  extracting: string | undefined
  typeaheadId: string | undefined
  query: string | undefined
  windowSearch: string
  pathname: string
  typeaheadTypes?: Array<App.SearchPlaceType>
  rollout?: Array<string>
}

interface UseTypeaheadExtractionUrlUpdateReturn {
  isExtracting: boolean
  isExtractionComplete: boolean
  isSearchBlocked: boolean
}

/**
 * Hook to handle typeahead extraction URL updates
 * - Checks if extraction is in progress
 * - Waits for extraction to complete
 * - Maps extracted data to URL params
 * - Updates URL and removes extracting=true when complete
 * - Blocks search while extraction is in progress
 */
export function useTypeaheadExtractionUrlUpdate({
  extracting,
  typeaheadId,
  query,
  windowSearch,
  pathname,
  typeaheadTypes,
  rollout,
}: UseTypeaheadExtractionUrlUpdateParams): UseTypeaheadExtractionUrlUpdateReturn {
  const dispatch = useAppDispatch()
  const location = useLocation()
  const streamingData = useAppSelector(state =>
    typeaheadId ? state.search.typeaheadStreamingData[typeaheadId] : undefined,
  )

  const isExtracting = extracting === 'true'

  // Handle page refresh during extraction - re-dispatch streamTypeahead if needed
  useTypeaheadExtractionRefresh({
    isExtracting,
    typeaheadId,
    query: query,
    typeaheadTypes,
    rollout,
  })

  // Check if extraction is complete
  const isExtractionComplete = useMemo(() => {
    if (!isExtracting || !streamingData || !query) {
      return false
    }
    return streamingData.isComplete && streamingData.completedInput === query
  }, [isExtracting, streamingData, query])

  // Update URL with extracted data when extraction completes
  useEffect(() => {
    if (!isExtractionComplete || !streamingData || !typeaheadId) {
      return
    }

    // Map extracted data to URL params (pass current search to preserve user-entered values)
    const paramsToSet = mapExtractedDataToUrlParams(streamingData, windowSearch)
    const finalSearchItem = streamingData.finalSearchItem?.data
    const extractedData = finalSearchItem?.searchType === 'searchItemV3' ? finalSearchItem.searchItem.extractedData : undefined
    const extractedDateSelection = getSearchDateSelectionFromExtractedSearchParams(extractedData)
    const baselineDateSelection = getTypeaheadDateConfirmationBaseline(typeaheadId)

    if (
      isSearchDateSelectionExtractedOverride(extractedDateSelection) &&
      baselineDateSelection &&
      !areSearchDateSelectionsEqual(baselineDateSelection, extractedDateSelection) &&
      !streamingData.userSelectedDates
    ) {
      markTypeaheadDateAcknowledgementRequired(typeaheadId)
    }

    // Preserve the current typeaheadId - it should stay in URL after extraction completes
    paramsToSet.push({ paramName: queryKeyTypeaheadId, paramValue: typeaheadId })

    // Clear all search params first (like a new search would)
    const clearedSearch = deleteSearchParams(windowSearch, ...searchQueryParams, queryKeyPages)

    // Set only the extracted params (including the preserved typeaheadId)
    const newSearch = setManySearchParamValues(clearedSearch, paramsToSet)

    // Preserve the current hash (e.g., #search_hotels) to keep the mobile search drawer open
    const currentHash = location.hash ? location.hash.slice(1) : undefined

    dispatch(replaceWithRegion(pathname, newSearch, currentHash))
  }, [isExtractionComplete, streamingData, typeaheadId, windowSearch, pathname, dispatch, query, location.hash])

  // Block search while extraction is in progress
  const isSearchBlocked = isExtracting && !isExtractionComplete

  return {
    isExtracting,
    isExtractionComplete,
    isSearchBlocked,
  }
}
